/* AUTO-SYNCED from PsyKit.io/components/session/CheckinComparison.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { classifyEmotions, ValenceMarker } from "@/components/session/EmotionPicker";

interface CheckinComparisonProps {
  /** Heading, e.g. "Pre-Session" or "At the start". */
  title?: string;
  /** Only the emotions that were actually selected. */
  emotions: string[];
  distress: number | null;
  capability: number | null;
  note?: string | null;
  goal?: string | null;
}

/**
 * Read-only summary of a submitted check-in, shown beside the live post
 * check-in so the therapist and client can see the change the session made.
 *
 * Shows ONLY the emotions that were selected, grouped Negative (red) / Positive
 * (green) with the same valence markers as the EmotionPicker, plus the
 * distress/capability readings and the note. No inputs — purely a snapshot.
 */
export default function CheckinComparison({
  title = "Pre-Session",
  emotions,
  distress,
  capability,
  note,
  goal,
}: CheckinComparisonProps) {
  const { negative, positive, other } = classifyEmotions(emotions);

  const chip = (emotion: string) => (
    <span
      key={emotion}
      className="rounded-[4px] font-[var(--font-barlow)] text-[12px] uppercase tracking-[0.06em] px-2.5 py-1.5"
      style={{ background: "#C9A84C22", border: "1px solid #C9A84C", color: "#C9A84C" }}
    >
      {emotion}
    </span>
  );

  const emptyHint = (
    <span className="font-[var(--font-dm-mono)] text-[10px]" style={{ color: "#3D5470" }}>
      —
    </span>
  );

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p
          className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.2em]"
          style={{ color: "#5A7A98" }}
        >
          {title}
        </p>
        <p
          className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.16em] mt-0.5"
          style={{ color: "#3D5470" }}
        >
          Submitted at the start
        </p>
      </div>

      {goal && (
        <div className="flex flex-col gap-1">
          <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>
            Goal
          </span>
          <span className="font-[var(--font-dm-mono)] text-[11px] leading-snug" style={{ color: "#8AABC8" }}>
            {goal}
          </span>
        </div>
      )}

      {/* Emotions — selected only, valence-grouped to mirror the picker */}
      <div
        className="rounded-[4px] px-3 py-3"
        style={{ border: "1px solid #1A2E4A", background: "#070C16" }}
      >
        <div className="flex justify-end">
          <ValenceMarker kind="negative" />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {negative.length > 0 ? negative.map(chip) : emptyHint}
        </div>

        <div className="my-3 border-t" style={{ borderColor: "#233D6B" }} />

        <div className="flex flex-wrap gap-1.5">
          {positive.length > 0 ? positive.map(chip) : emptyHint}
        </div>
        <div className="flex justify-end mt-2">
          <ValenceMarker kind="positive" />
        </div>

        {other.length > 0 && (
          <>
            <div className="my-3 border-t" style={{ borderColor: "#1A2E4A" }} />
            <div className="flex flex-wrap gap-1.5">{other.map(chip)}</div>
          </>
        )}
      </div>

      {/* Scales */}
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-2">
          <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>
            Distress
          </span>
          <span className="font-[var(--font-dm-mono)] text-[12px]" style={{ color: "#8AABC8" }}>
            {distress !== null ? `${distress}/10` : "—"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>
            Capability
          </span>
          <span className="font-[var(--font-dm-mono)] text-[12px]" style={{ color: "#8AABC8" }}>
            {capability !== null ? `${capability}/10` : "—"}
          </span>
        </div>
      </div>

      {note && note.trim() && (
        <div className="flex flex-col gap-1">
          <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.14em]" style={{ color: "#3D5470" }}>
            Note
          </span>
          <p className="font-[var(--font-barlow)] text-[12px] leading-relaxed" style={{ color: "#8AABC8" }}>
            {note}
          </p>
        </div>
      )}
    </div>
  );
}
